import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { InfoOutline } from '@yme/assets/icons';
import { ICON_SIZE, SVGIcon } from '@yme/atoms/svg-icon';
import { Tooltip } from '@yme/atoms/tooltip';
import { useWindowSize } from '@yme/hooks/useWindowSize';
import { setIsModalShowed } from '@yme/modals/actions';
import { MODALS } from '@yme/modals/constants';
import { getCompatibilityCheckAllowed, getProductCardLabels, } from '@yme/pdp-components/ProductCard';
import { getRequiredProductVariantBySku } from '@yme/pdp-components/ProductPart/helpers';
import { getIsInAuthor, getRootLang, getRTL } from '@yme/selectors/common';
import { colorCoreBlack900Base } from '@yme/tokens/colors';
import { FlexWrapper } from '@yme/tokens/flexTokens';
import { RegularLinkLevel1 } from '@yme/tokens/typography';
import { CheckCompatibility } from '../../CardAccessories/CheckCompatibility';
import { PRODUCT_PART_CLASS_NAME } from './constants';
import { getCheckedStatusById, getProductsCheckedStatus } from './heplers';
import { CheckboxStyled, RequiredProductPartStyled, RequiredPartsContainerStyled, RequiredPartsContent, RequiredPartsHeader, RequiredPartsTitle, } from './styled';
export function RequiredPartsContainer({ requiredProducts, labels, plpUrl, selectModelHandler, addRequiredProduct, removeRequiredProduct, privacyConsentsModel, }) {
    const [isAddAllCheckboxChecked, setIsAddAllCheckboxChecked] = useState(false);
    const [isSomeNotChecked, setIsSomeNotChecked] = useState(false);
    const [addedRequiredProducts, setAddedRequiredProducts] = useState([]);
    const ref = useRef(null);
    const isScrollable = ref.current
        ? ref.current.scrollHeight + 20 > ref.current.clientHeight
        : false;
    const isRequiredPartsWithItems = Boolean(requiredProducts.length);
    const addAllButtonVisible = Boolean(requiredProducts.length > 1);
    const { isInfiniteDesktopView } = useWindowSize();
    const dispatch = useDispatch();
    const isRightToLeft = useSelector(getRTL);
    const rootLanguage = useSelector(getRootLang);
    const isInAuthor = useSelector(getIsInAuthor);
    const isCompatibilityCheckAllowed = useSelector(getCompatibilityCheckAllowed);
    const { productCard_controlMethod_label: controlMethod, productCard_startingMethodAndTilt_label: startingMethod, productCard_shaftLength_label: shaftLength, productCard_rotation_label: rotation, productCard_colorSelector_label: colourName, product_size_label: size, } = useSelector(getProductCardLabels);
    const productPartLabels = {
        controlMethod,
        startingMethod,
        shaftLength,
        rotation,
        colourName,
        size,
        outOfStockWarning: labels?.partsAccessoriesMerch_outOdStockWarning_label,
        notifyWhenInStockLabel: labels?.partsAccessoriesMerch_notifyWhenInStock_label,
        ...labels,
    };
    useEffect(() => {
        if (isRequiredPartsWithItems) {
            const requiredProductsWithPricesAndStock = requiredProducts
                .filter((product) => {
                const { prices, stock } = getRequiredProductVariantBySku(product.variants, '');
                const [price] = prices;
                return price && stock !== 0;
            })
                .map((product) => ({
                id: product.id,
                checked: false,
            }));
            setAddedRequiredProducts(requiredProductsWithPricesAndStock);
        }
    }, [requiredProducts, isRequiredPartsWithItems]);
    const setProductsCheckedStatus = (checked) => {
        setAddedRequiredProducts((prevState) => prevState.map((prev) => ({
            ...prev,
            checked,
        })));
    };
    const onChangeCheckbox = ({ target }) => {
        setProductsCheckedStatus(target.checked);
    };
    const checkRequiredProductAdded = (id, checked) => {
        setAddedRequiredProducts((prevState) => prevState.map((prevStateProduct) => {
            if (prevStateProduct.id === id) {
                return {
                    id,
                    checked,
                };
            }
            return prevStateProduct;
        }));
    };
    useEffect(() => {
        const { isSomeAdded, isEveryNotAdded, isSomeNotAdded, isEveryAdded } = getProductsCheckedStatus(addedRequiredProducts);
        if (isEveryAdded) {
            setIsSomeNotChecked(false);
        }
        if (isSomeAdded) {
            setIsAddAllCheckboxChecked(true);
        }
        if (isSomeNotAdded) {
            setIsSomeNotChecked(true);
        }
        if (isEveryNotAdded) {
            setIsSomeNotChecked(false);
            setIsAddAllCheckboxChecked(false);
        }
    }, [addedRequiredProducts]);
    const handleRequiredPartsInfo = useCallback(() => {
        dispatch(setIsModalShowed({
            type: MODALS.INFO_MODAL,
            data: {
                title: labels?.partsAccessoriesMerch_requiredParts_title,
                description: labels?.partsAccessoriesMerch_requiredParts_hint,
            },
        }));
    }, [
        dispatch,
        labels?.partsAccessoriesMerch_requiredParts_hint,
        labels?.partsAccessoriesMerch_requiredParts_title,
    ]);
    return (React.createElement(FlexWrapper, { flexDirection: "column", gap: "24px", alignItems: "flex-start", ptDesktop: "24px", pbDesktop: "24px" },
        React.createElement(RequiredPartsContainerStyled, { flexDirection: "column" },
            React.createElement(RequiredPartsHeader, { isRightToLeft: isRightToLeft, flexDirection: "row", justifyContent: "space-between" },
                React.createElement(FlexWrapper, { flexDirection: "row", gap: "8px", alignItems: "center" },
                    React.createElement(RequiredPartsTitle, null, labels?.partsAccessoriesMerch_requiredParts_title),
                    isInfiniteDesktopView ? (React.createElement(Tooltip, { title: labels?.partsAccessoriesMerch_requiredParts_hint },
                        React.createElement(SVGIcon, { type: InfoOutline, pathFill: colorCoreBlack900Base, size: ICON_SIZE.xs }))) : (React.createElement(SVGIcon, { type: InfoOutline, pathFill: colorCoreBlack900Base, size: ICON_SIZE.xs, onClick: handleRequiredPartsInfo }))),
                addAllButtonVisible && isInfiniteDesktopView && (React.createElement(CheckboxStyled, { onChange: onChangeCheckbox, hasMultipleChecked: isSomeNotChecked, checked: isAddAllCheckboxChecked, isRightToLeft: !isRightToLeft, value: labels?.common_addAll_label }))),
            React.createElement(RequiredPartsContent, { withScroll: true, ref: ref, isRightToLeft: isRightToLeft, isScrollable: isScrollable, isRequiredPartsWithItems: isRequiredPartsWithItems, flexDirection: "column", alignItems: "center" }, isRequiredPartsWithItems ? (requiredProducts.map((product) => (React.createElement(RequiredProductPartStyled, { key: product.id, item: product, isScrollable: isScrollable, className: PRODUCT_PART_CLASS_NAME, labels: productPartLabels, rootLanguage: rootLanguage, checkRequiredProductAdded: checkRequiredProductAdded, isChecked: getCheckedStatusById(addedRequiredProducts, product.id), addRequiredProduct: addRequiredProduct, removeRequiredProduct: removeRequiredProduct, privacyConsentsModel: privacyConsentsModel, isInAuthor: isInAuthor, isRightToLeft: isRightToLeft })))) : (React.createElement(RegularLinkLevel1, { onClick: selectModelHandler, color: colorCoreBlack900Base }, labels?.partsAccessoriesMerch_selectModelToView_link)))),
        isRequiredPartsWithItems && isCompatibilityCheckAllowed && plpUrl && (React.createElement(CheckCompatibility, { link: plpUrl, isRightToLeft: isRightToLeft, alertText: labels?.partsAccessoriesMerch_conflictingPatsAlert_description, linkLabel: labels?.partsAccessoriesMerch_seeParts_label }))));
}
